import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from './IntelligenceLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Typeahead company search — a plain <select> would need 3,000+ <option> tags.
export const CompanyPicker = ({ value, onChange, testId = 'company-picker' }) => {
  const [query, setQuery] = useState(value?.name || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!query || query === value?.name) { setResults([]); return; }
    const t = setTimeout(() => {
      fetchWithAuth(`${API_URL}/api/crm/companies?search=${encodeURIComponent(query)}&limit=8`)
        .then(d => { setResults(d.companies); setOpen(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative">
      <input value={query} onChange={e => { setQuery(e.target.value); onChange(null); }} onFocus={() => results.length && setOpen(true)}
        placeholder="Search company *" data-testid={testId}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#D4AF37]" />
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {results.map(c => (
            <button key={c.id} type="button" onClick={() => { onChange(c); setQuery(c.name); setOpen(false); }} data-testid={`${testId}-option-${c.id}`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#fdf9ef] truncate block">{c.name}</button>
          ))}
        </div>
      )}
    </div>
  );
};

// Contact select scoped to a chosen company — few enough per company for a plain <select>.
export const ContactSelect = ({ companyId, value, onChange, testId = 'contact-select', placeholder = 'Contact (optional)' }) => {
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    if (!companyId) { setContacts([]); return; }
    fetchWithAuth(`${API_URL}/api/crm/contacts?company_id=${companyId}&limit=50`).then(d => setContacts(d.contacts)).catch(() => {});
  }, [companyId]);

  return (
    <select value={value || ''} onChange={e => onChange(e.target.value || null)} disabled={!companyId} data-testid={testId}
      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#D4AF37] bg-white disabled:bg-gray-50">
      <option value="">{companyId ? placeholder : 'Select a company first'}</option>
      {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name || ''}{c.title ? ` — ${c.title}` : ''}</option>)}
    </select>
  );
};

// Agreement select scoped to a chosen company.
export const AgreementSelect = ({ companyId, value, onChange, testId = 'agreement-select' }) => {
  const [agreements, setAgreements] = useState([]);

  useEffect(() => {
    if (!companyId) { setAgreements([]); return; }
    fetchWithAuth(`${API_URL}/api/crm/agreements?company_id=${companyId}&limit=50`).then(d => setAgreements(d.agreements)).catch(() => {});
  }, [companyId]);

  return (
    <select value={value || ''} onChange={e => onChange(e.target.value || null)} disabled={!companyId} data-testid={testId}
      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#D4AF37] bg-white disabled:bg-gray-50">
      <option value="">{companyId ? 'Agreement (optional)' : 'Select a company first'}</option>
      {agreements.map(a => <option key={a.id} value={a.id}>{a.status} · {a.fee_percentage}% fee</option>)}
    </select>
  );
};

// Job Order typeahead search — used to attach a standalone revenue record.
export const JobOrderPicker = ({ value, onChange, testId = 'job-order-picker' }) => {
  const [query, setQuery] = useState(value?.job_title || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!query || query === value?.job_title) { setResults([]); return; }
    const t = setTimeout(() => {
      fetchWithAuth(`${API_URL}/api/crm/job-orders?search=${encodeURIComponent(query)}&limit=8`)
        .then(d => { setResults(d.job_orders); setOpen(true); }).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="relative">
      <input value={query} onChange={e => { setQuery(e.target.value); onChange(null); }} onFocus={() => results.length && setOpen(true)}
        placeholder="Search job order *" data-testid={testId}
        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#D4AF37]" />
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {results.map(j => (
            <button key={j.id} type="button" onClick={() => { onChange(j); setQuery(j.job_title); setOpen(false); }} data-testid={`${testId}-option-${j.id}`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-[#fdf9ef] truncate block">{j.job_title} — {j.company_name}</button>
          ))}
        </div>
      )}
    </div>
  );
};
